/**
 * A deal's history, newest first: who did what, and what changed from what.
 *
 * Read from the deal's own audit rows (`GET /api/crm/deals/:id/history`), so
 * there is nothing to keep in step — the history is whatever was recorded.
 * "Show older" pages backwards with the cursor the server hands back.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { CrmHistoryEntryDto } from '@forsys/shared';
import { AsyncContent } from '../../components/AsyncContent';
import { ErrorState } from '../../components/ErrorState';
import { Panel } from '../../components/Panel';
import { Timestamp } from '../../components/Timestamp';
import { useAsync } from '../../hooks/useAsync';
import { useSubmit } from '../../hooks/useSubmit';
import { getDealHistory } from './api';
import { formatDay } from './format';

/** The deal events this screen has words for. Anything else shows its raw name. */
const EVENTS = ['created', 'updated', 'reassigned', 'moved', 'won', 'lost', 'reopened'] as const;
type KnownEvent = (typeof EVENTS)[number];

/** The fields a change line names. Ids are left out: the names beside them say the same. */
const FIELDS = [
  'title',
  'value',
  'currency',
  'stage',
  'person',
  'organization',
  'expectedCloseDate',
  'owner',
  'lostReason',
] as const;
type KnownField = (typeof FIELDS)[number];

function known<T extends string>(list: readonly T[], value: string): value is T {
  return (list as readonly string[]).includes(value);
}

export function DealHistory({ dealId, version }: { dealId: number; version: unknown }) {
  const { t } = useTranslation('crm');
  // `version` changes when the deal does, so a move shows up here at once.
  const first = useAsync(() => getDealHistory(dealId), [dealId, version]);
  const [older, setOlder] = useState<{ events: CrmHistoryEntryDto[]; next: string | null }>({ events: [], next: null });

  useEffect(() => {
    setOlder({ events: [], next: first.data?.nextBeforeId ?? null });
  }, [first.data]);

  const more = useSubmit(async () => {
    if (older.next === null) return undefined;
    const page = await getDealHistory(dealId, older.next);
    setOlder({ events: [...older.events, ...page.events], next: page.nextBeforeId });
    return page;
  });

  const shown = (value: unknown, field: KnownField): string => {
    if (value === null || value === undefined || value === '') return t('history.none');
    if (field === 'expectedCloseDate' && typeof value === 'string') return formatDay(value);
    return String(value);
  };

  function describe(entry: CrmHistoryEntryDto): string {
    const after = entry.after ?? {};
    const before = entry.before ?? {};
    return Object.keys(after)
      .filter((key): key is KnownField => known(FIELDS, key))
      .map((key) =>
        entry.event.endsWith('.created')
          ? `${t(`history.field.${key}`)}: ${shown(after[key], key)}`
          : `${t(`history.field.${key}`)}: ${shown(before[key], key)} → ${shown(after[key], key)}`,
      )
      .join(' · ');
  }

  function title(entry: CrmHistoryEntryDto): string {
    const short = entry.event.split('.').at(-1) ?? entry.event;
    return known<KnownEvent>(EVENTS, short) ? t(`history.event.${short}`) : entry.event;
  }

  return (
    <Panel title={t('history.title')}>
      <AsyncContent state={first}>
        {({ events }) => (
          <Stack spacing={1.25}>
            {[...events, ...older.events].map((entry) => (
              <div key={entry.id}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {title(entry)}
                </Typography>
                {describe(entry) && (
                  <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                    {describe(entry)}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary">
                  {entry.actorLabel} · <Timestamp value={entry.occurredAt} />
                </Typography>
              </div>
            ))}
            {more.error && <ErrorState error={more.error} />}
            {older.next !== null && (
              <Button size="small" onClick={() => void more.submit()} disabled={more.pending} sx={{ alignSelf: 'flex-start' }}>
                {t('history.older')}
              </Button>
            )}
          </Stack>
        )}
      </AsyncContent>
    </Panel>
  );
}
